import React, { useCallback, useMemo, useState } from "react";
import { Button, Space } from "antd";
import dayjs from "dayjs";
import { RedoOutlined } from "@ant-design/icons";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { binDBKeys } from "src/pages/binOverview/BinCommander";
import { FansChart, FanStatusEnum, usefanAndHeaterChartQueryOptions } from "./FanAndHeaterChart";
import { ChartDatePicker } from "./Co2Chart";
import { CsvButton } from "./csv/CsvButton";
import { BaseChartProps } from "./TemperatureCableChart";
import BinApiService from "src/api/BinApiService";
import { ChartDataset } from "chart.js";
import { BaseChartQueryOptions, BaseCsv, useAddDefaultAnnotationsToCsv, useModeChangeHistory } from "./modehistoryAndFanChangeHistory";
import { useFanHeaterChangeHistory } from "./shared";
import { chartCsvHeaders, timestampHeader } from "./csv/core";
import { BaseHeaders } from "./csv/types";


export const FansCsv = (props: BaseChartQueryOptions & { hasHeater: boolean }) => {

    const chartQuery = useQuery({
        ...usefanAndHeaterChartQueryOptions({
            binId: props.binId,
            range: props.range,
        }),
    });



    const data: BaseCsv[] = [];
    const headers: BaseHeaders = [timestampHeader];
    let additionalHeaders: string[] = [];

    chartQuery.data?.fans?.forEach((fan, fanIndex) => {
        const fanId = fan.label;
        additionalHeaders.push(chartCsvHeaders.fanAmps(fanId))
        if (props.hasHeater) {
            additionalHeaders.push(chartCsvHeaders.fanHeaterAmps(fanId));
        }
        // additionalHeaders.push(chartCsvHeaders.fanPlenumPressureInH2O(fanId));
        additionalHeaders.push(chartCsvHeaders.fanIsOn(fanId));
        if (props.hasHeater) {
            additionalHeaders.push(chartCsvHeaders.heaterStatus(fanId));
        }
    });


    const timeSeries = chartQuery.data?.fans?.[0]?.data?.map(row => row.x) ?? [];

    timeSeries.forEach((timestamp, index) => {
        const newRow: BaseCsv = { [timestampHeader]: timestamp };

        chartQuery.data?.fans?.forEach((fan, fanIndex) => {
            const fanId = fan.label;
            newRow[chartCsvHeaders.fanAmps(fanId)] = fan.data?.[index]?.fanAmps;
            newRow[chartCsvHeaders.fanHeaterAmps(fanId)] = fan.data?.[index]?.heaterAmps;
            newRow[chartCsvHeaders.fanPlenumPressureInH2O(fanId)] = fan.data?.[index]?.plenumPressureInH2O;
            newRow[chartCsvHeaders.fanIsOn(fanId)] = fan.data?.[index]?.fanIsOn ? FanStatusEnum.ON : fan.data?.[index]?.fanIsOn === false ? FanStatusEnum.OFF : null;
            newRow[chartCsvHeaders.heaterStatus(fanId)] = fan.data?.[index]?.heaterIsOn ? FanStatusEnum.ON : fan.data?.[index]?.heaterIsOn === false ? FanStatusEnum.OFF : null;
        });
        data.push(newRow);

    })

    headers.push(...additionalHeaders);


    const withAnnotions = useAddDefaultAnnotationsToCsv({
        baseData: data,
        baseHeaders: headers,
        binId: props.binId,
        range: props.range,
    });
    const finalCsv = withAnnotions;



    return <CsvButton data={finalCsv.data} headers={finalCsv.headers} baseFileName="fans" />;
};

export const FansChartTab = (props: BaseChartProps & {hasHeater: boolean}) => {
    const currentDate = dayjs();
    const priorDate = currentDate.subtract(7, 'days');
    const isControlled = typeof props.value !== 'undefined';
    const [internalValue, setInternalValue] = useState<[any, any]>([priorDate, currentDate]);
    const inprogress = useIsFetching([...binDBKeys.bin(props.binId), "charts"]);
    const value = isControlled ? props.value : internalValue;

    const onDateChange = useCallback((values: [any, any], formatter?: any) => {
        if (props.onChange) {
            props.onChange(values);
        } else {
            setInternalValue(values);
        }
    }, [props.onChange]);

    return (
        <div>
            <Space direction="horizontal" wrap>
                <ChartDatePicker startDate={value?.[0]} endDate={value?.[1]} onDateRangeChange={onDateChange} />
                <Button
                    type="primary"
                    disabled={inprogress > 0}
                    icon={<RedoOutlined spin={inprogress > 0} />}
                    onClick={() => {
                        onDateChange([value?.[0], dayjs()], ['', '']);
                    }}
                />
                <FansCsv binId={props.binId} range={value} hasHeater={props.hasHeater} />
            </Space>
            <div style={{ width: "100%" }}>
                <FansChart binId={props.binId} deviceId={props.deviceId} hasHeater={props.hasHeater}  value={value} />
            </div>
        </div>
    );
};
